import { AudioClip, AudioSource, Tween, Node, NodePool, director, game, Game, sys, EventTarget, warn, debug } from 'cc';
import VoiceAssetProvider from './AudioManager/VoiceAssetProvider';
import VoiceLanguage from './AudioManager/VoiceLanguage';
import BundleAssetProvider from './BundleAssetProvider';
import AssetLoadingConfigType from './Config/AssetLoadingConfigType';
import IPreloader from './IPreloader';
import Delay from './Toolkit/Utils/Delay';
import Processor from './Toolkit/Components/Misc/Processor';
import CompositeLoadingTask from './Toolkit/Helper/Loading/LoadingTask/CompositeLoadingTask';

enum EventType {
    MUSIC_MUTE_CHANGE = 'MUSIC_MUTE_CHANGE',
    MUSIC_VOLUME_CHANGE = 'MUSIC_VOLUME_CHANGE',
    EFFECT_MUTE_CHANGE = 'EFFECT_MUTE_CHANGE',
    EFFECT_VOLUME_CHANGE = 'EFFECT_VOLUME_CHANGE',
    PLAY_SOUND_EFFECT = 'PLAY_SOUND_EFFECT',
    STOP_SOUND_EFFECT = 'STOP_SOUND_EFFECT',
}

type FullAudioConfig = {
    musicVolumeGain: number;
    effectVolumeGain: number;
    clip: AudioClipConfig;
};
export type AudioConfig = Partial<FullAudioConfig>;
export type AudioClipConfig = { [name: string]: { coldDown?: number; maxPlayingCount?: number; volumeGain?: number } };

type AudioInfo = {
    audio: any;
    playTime: number;
};
type AudioSourceWithPlayTime = AudioSource & { playTime: number };
export type ChooseAudioSourceToDiscardCallback = (audioSources: AudioSourceWithPlayTime[]) => AudioSource;

const defaultChooseAudioSourceToDiscard: ChooseAudioSourceToDiscardCallback = (audioSources) =>
    audioSources.reduce((targetAudioSource, audioSource) => {
        // choose the audio source with the oldest play time to discard
        if (!targetAudioSource || targetAudioSource.playTime < audioSource.playTime) {
            return audioSource;
        }
        return targetAudioSource;
    }, null as AudioSourceWithPlayTime);

class AudioClipState {
    public static readonly DEFAULT_COLD_DOWN = 50;
    public static readonly DEFAULT_MAX_PLAYING_COUNT = 10;

    public passFilter: boolean;
    private readonly _playingAudioSources: AudioSource[];
    private _lastPlayTime: number;

    constructor(
        public readonly name: string,
        passFilter: boolean,
        public readonly volumeGain: number = -1, // default -1 means using default volume gain
        private readonly _coldDown: number = AudioClipState.DEFAULT_COLD_DOWN,
        private readonly _maxPlayingCount: number = AudioClipState.DEFAULT_MAX_PLAYING_COUNT,
    ) {
        this._lastPlayTime = 0;
        this._playingAudioSources = [];
        this.passFilter = passFilter;
        if (volumeGain < 0 && volumeGain !== -1) {
            warn(`Invalid volume gain: ${volumeGain} for audio clip: ${name}, using default volume gain instead.`);
            this.volumeGain = -1; // reset to default
        }
    }

    public get playingCount() {
        return this._playingAudioSources.length;
    }

    public addPlaying(audioSource: AudioSource) {
        const index = this._playingAudioSources.indexOf(audioSource);
        if (index !== -1) {
            return;
        }
        this._playingAudioSources.push(audioSource);
    }

    public removePlaying(audioSource: AudioSource) {
        const index = this._playingAudioSources.indexOf(audioSource);
        if (index !== -1) {
            this._playingAudioSources.splice(index, 1);
        }
    }

    public canPlay(): boolean {
        if (this.playingCount >= this._maxPlayingCount) {
            return false;
        }
        const currentTime = Date.now();
        if (currentTime - this._lastPlayTime <= this._coldDown) {
            return false;
        }
        this._lastPlayTime = currentTime;
        return true;
    }
}

export default class AudioManager<TMusic extends string = string, TEffect extends string = string, TVoice extends string = string>
    extends EventTarget
    implements IPreloader
{
    public static readonly EVENT_TYPE = EventType;

    private readonly _assetProvider: BundleAssetProvider = null;
    private readonly _voiceAssetProvider: VoiceAssetProvider = null;
    private readonly _audioClipStateMap: { [name: string]: AudioClipState } = {};
    private readonly _audioSourcePoolMap: { [audioClipName: string]: NodePool } = {};
    private readonly _musicAudioSources: AudioSource[] = [];
    private readonly _interruptedAudioSources: AudioSource[] = [];
    private readonly _stopFadingVolumeCallbacks: Map<AudioSource, () => void> = new Map();
    private _audioSourceRoot: Node = null;
    private _initVoiceLanguage: VoiceLanguage = null;
    private _isFilterEnabled: boolean = false;
    private _chooseAudioSourceToDiscard: ChooseAudioSourceToDiscardCallback;

    private readonly defaultMusicVolumeGain: number = 1;
    private readonly defaultEffectVolumeGain: number = 1;
    // 音量
    private _effectVolume: number = 1;
    private _musicVolume: number = 1;
    // 所有靜音
    private _allMute: boolean = false;
    // 靜音
    private _effectMute: boolean = false;
    private _musicMute: boolean = false;

    constructor(config: AssetLoadingConfigType | AssetLoadingConfigType[] | BundleAssetProvider);
    constructor(
        config: AssetLoadingConfigType | AssetLoadingConfigType[] | BundleAssetProvider,
        voiceAssetProvider: AssetLoadingConfigType | VoiceAssetProvider,
        initLanguage: VoiceLanguage,
    );
    constructor(config: AssetLoadingConfigType | AssetLoadingConfigType[] | BundleAssetProvider, audioConfig: AudioConfig);
    constructor(
        config: AssetLoadingConfigType | AssetLoadingConfigType[] | BundleAssetProvider,
        voiceAssetProvider: AssetLoadingConfigType | VoiceAssetProvider,
        initLanguage: VoiceLanguage,
        audioConfig: AudioConfig,
    );
    constructor(
        config: AssetLoadingConfigType | AssetLoadingConfigType[] | BundleAssetProvider,
        arg2?: AssetLoadingConfigType | VoiceAssetProvider | AudioConfig,
        arg3?: VoiceLanguage,
        arg4?: AudioConfig,
    ) {
        super();

        // handle arguments
        let voiceAssetProvider: VoiceAssetProvider;
        if (arg2 instanceof VoiceAssetProvider) {
            voiceAssetProvider = arg2;
        } else if (arg3) {
            voiceAssetProvider = new VoiceAssetProvider(arg2 as AssetLoadingConfigType);
        }
        const audioConfig = (arg4 ?? (voiceAssetProvider ? undefined : arg2)) as AudioConfig;

        // apply audio config
        if (audioConfig) {
            this.defaultMusicVolumeGain = audioConfig.musicVolumeGain ?? 1;
            this.defaultEffectVolumeGain = audioConfig.effectVolumeGain ?? 1;
            if (audioConfig.clip) {
                Object.entries(audioConfig.clip).forEach(([name, { coldDown, maxPlayingCount, volumeGain }]) => {
                    this._audioClipStateMap[name] = new AudioClipState(name, true, volumeGain, coldDown, maxPlayingCount);
                });
            }
        }

        // to fix sound disappear after returning from background on iOS
        if (sys.os === sys.OS.IOS) {
            game.on(Game.EVENT_SHOW, () => {
                this._interruptedAudioSources.forEach((audioSource) => audioSource.play());
                this._interruptedAudioSources.length = 0;
            });
            game.on(Game.EVENT_HIDE, () => {
                const audioSources = [...this.audioSourceRoot.getComponentsInChildren(AudioSource)];
                this._interruptedAudioSources.push(...audioSources.filter((audioSource) => audioSource.state === AudioSource.AudioState.PLAYING));
                this._interruptedAudioSources.forEach((audioSource) => audioSource.pause());
            });
        }

        // init music and effect volume
        let originalMusicMute: boolean = this.musicMute;
        let originalEffectMute: boolean = this.effectMute;
        game.on(Game.EVENT_SHOW, () => {
            this.musicMute = originalMusicMute;
            this.effectMute = originalEffectMute;
        });
        game.on(Game.EVENT_HIDE, () => {
            originalMusicMute = this.musicMute;
            originalEffectMute = this.effectMute;
            this.musicMute = true;
            this.effectMute = true;
        });

        // initialize asset provider
        if (config instanceof BundleAssetProvider) {
            this._assetProvider = config;
        } else {
            this._assetProvider = new BundleAssetProvider(config);
        }
        const initLanguage = arg3;
        this._voiceAssetProvider = voiceAssetProvider;
        if (voiceAssetProvider && !initLanguage) {
            throw new Error('initLanguage is required when voiceAssetProvider is set');
        }
        this._initVoiceLanguage = initLanguage;

        // handle audio discard callback
        (window as any).cc.audioManager.getAudioInfoToDiscardCallback = () => {
            // prepare candidates for discarding
            const map: Map<AudioSource, AudioInfo> = new Map();
            const infoList: AudioInfo[] = (window as any).cc.audioManager._audioPlayerInfoList;
            const audioSourceCandidates: AudioSourceWithPlayTime[] = this.playingEffects as AudioSourceWithPlayTime[];
            [...audioSourceCandidates].forEach((audioSource: AudioSourceWithPlayTime) => {
                const targetInfo = infoList.find((info: AudioInfo) => info.audio === (audioSource as any)._player);
                if (!targetInfo) {
                    // no info found, which means the audio source has not been played yet
                    const index = audioSourceCandidates.indexOf(audioSource);
                    audioSourceCandidates.splice(index, 1);
                    return;
                }
                audioSource.playTime = targetInfo ? targetInfo.playTime : Number.MAX_SAFE_INTEGER;
                map.set(audioSource, targetInfo);
            });

            if (audioSourceCandidates.length === 0) {
                warn('No audio source can be discarded.');
                return null;
            }

            // choose the target audio source to discard
            const chooseAudioSourceToDiscard = this._chooseAudioSourceToDiscard ?? defaultChooseAudioSourceToDiscard;
            let targetAudioSource = chooseAudioSourceToDiscard(audioSourceCandidates);
            if (!targetAudioSource) {
                warn('No discard audio source selected by chooseAudioSourceToDiscard callback, discarding the first one instead.');
                targetAudioSource = this.playingEffects[0];
            }
            debug(`Discarding audio source: ${targetAudioSource.usedClip.name}`);
            this.recycleAudioSource(targetAudioSource);
            // return null to skip the default audio source discard logic
            return null;
        };
    }

    public get initVoiceLanguage() {
        return this._initVoiceLanguage;
    }
    public set initVoiceLanguage(value: VoiceLanguage) {
        if (this.currentVoiceLanguage) {
            warn('Can not change init language after language is loaded');
            return;
        }
        if (this._initVoiceLanguage === value) {
            return;
        }
        this._initVoiceLanguage = value;
    }

    public get currentVoiceLanguage() {
        if (!this._voiceAssetProvider) {
            warn('No voice asset provider is set');
            return null;
        }
        return this._voiceAssetProvider.currentLanguage;
    }

    public get effectVolume(): number {
        return this.effectMute ? 0 : this._effectVolume;
    }
    public set effectVolume(value: number) {
        if (this._effectVolume === value) {
            return;
        }
        this._effectVolume = value;
        this._updateEffectVolume();
        this.emit(EventType.EFFECT_VOLUME_CHANGE);
    }

    public get rawEffectVolume(): number {
        return this._effectVolume;
    }

    public get musicVolume(): number {
        return this.musicMute ? 0 : this._musicVolume;
    }
    public set musicVolume(value: number) {
        if (this._musicVolume === value) {
            return;
        }
        this._musicVolume = value;
        this._updateMusicVolume();
        this.emit(EventType.MUSIC_VOLUME_CHANGE);
    }

    public get rawMusicVolume(): number {
        return this._musicVolume;
    }

    /**
     * ⚠️ 此方法僅供 CRM 呼叫，用於關閉主遊戲的音樂/音效
     *    請勿從主遊戲調用，否則可能造成音樂狀態不同步
     */
    public set allMute(mute: boolean) {
        if (this._allMute === mute) {
            return;
        }
        this._allMute = mute;
        this._updateEffectVolume();
        this._updateMusicVolume();
    }

    public get allMute(): boolean {
        return this._allMute;
    }

    public get effectMute(): boolean {
        return this._effectMute;
    }

    public set effectMute(mute: boolean) {
        if (this._effectMute === mute) {
            return;
        }
        this._effectMute = mute;
        this._updateEffectVolume();
        this.emit(EventType.EFFECT_MUTE_CHANGE);
    }

    public get musicMute(): boolean {
        return this._musicMute;
    }

    public set musicMute(mute: boolean) {
        if (this._musicMute === mute) {
            return;
        }
        this._musicMute = mute;
        this._updateMusicVolume();
        this.emit(EventType.MUSIC_MUTE_CHANGE);
    }

    public get musicAudioSources() {
        return this._musicAudioSources;
    }

    public get isFilterEnabled() {
        return this._isFilterEnabled;
    }

    public get volumeOnlyPassFilters() {
        if (!this.isFilterEnabled) {
            return [];
        }
        return Object.values(this._audioClipStateMap)
            .filter((state) => state.passFilter)
            .map((state) => state.name);
    }
    public set volumeOnlyPassFilters(value: string[]) {
        value ??= [];
        this._isFilterEnabled = value.length !== 0;
        if (this._isFilterEnabled) {
            Object.values(this._audioClipStateMap).forEach((state) => {
                state.passFilter = false;
            });
            value.forEach((name) => {
                const state = this._getAudioClipState(name);
                state.passFilter = true;
            });
        } else {
            Object.values(this._audioClipStateMap).forEach((state) => {
                state.passFilter = true;
            });
        }

        this._updateEffectVolume();
        this._updateMusicVolume();
    }

    public get playingEffects() {
        return this.audioSourceRoot.getComponentsInChildren(AudioSource).filter((audioSource) => !this._musicAudioSources.includes(audioSource));
    }

    public set chooseAudioSourceToDiscard(fn: ChooseAudioSourceToDiscardCallback) {
        if (this._chooseAudioSourceToDiscard === fn) {
            return;
        }
        if (typeof fn !== 'function') {
            throw new Error('chooseAudioSourceToDiscard must be a function');
        }
        this._chooseAudioSourceToDiscard = fn;
    }

    private get audioSourceRoot() {
        if (!this._audioSourceRoot) {
            this._audioSourceRoot = new Node('AudioSourceRoot');
            director.addPersistRootNode(this.audioSourceRoot);
        }
        return this._audioSourceRoot;
    }

    public preload() {
        const task = this._assetProvider.preload() as CompositeLoadingTask;
        if (this._voiceAssetProvider) {
            this._voiceAssetProvider.loadLanguage(this._initVoiceLanguage);
            task.addTask(this._voiceAssetProvider.preload());
        }
        return task;
    }

    public addClips(assetProvider: BundleAssetProvider, clipConfig?: AudioClipConfig) {
        const clips = assetProvider.getAll(AudioClip);
        Object.entries(clips).forEach(([name, clip]) => this._assetProvider.set(name, AudioClip, clip));
        if (clipConfig) {
            Object.entries(clipConfig).forEach(([name, { coldDown, maxPlayingCount, volumeGain }]) => {
                this._audioClipStateMap[name] = new AudioClipState(name, true, volumeGain, coldDown, maxPlayingCount);
            });
        }
    }

    /**
     * 新增單一 clip，供執行期動態載入的音訊使用
     * @param name 播放時使用的名稱
     * @param clip 音訊
     */
    public addClip(name: string, clip: AudioClip) {
        this._assetProvider.set(name, AudioClip, clip);
    }

    /**
     * 移除單一 clip 的註冊，並銷毀回收池中使用該 clip 的 AudioSource
     * @param name 註冊時使用的名稱
     * @description 回收池中的 AudioSource 仍持有 clip 的 player，不銷毀的話 clip 釋放後解碼資料仍會留在記憶體;
     * clip 本身的釋放由載入方負責 (assetManager.releaseAsset)
     */
    public removeClip(name: string) {
        const clip = this._assetProvider.get(name, AudioClip);
        this._assetProvider.clear(name, AudioClip);
        new Set([name, clip?.name]).forEach((poolName) => {
            this._audioSourcePoolMap[poolName]?.clear();
            delete this._audioSourcePoolMap[poolName];
        });
    }

    public filterVolume(source: AudioSource): number {
        let clip: AudioClip;
        if (source instanceof AudioSource) {
            clip = source.clip ?? source.usedClip;
        } else {
            clip = this._getAudioClip(source);
        }
        if (!clip) {
            if (source instanceof AudioSource) {
                warn("Can't not find clip for source: ", source);
            } else {
                warn("Can't not find clip which name is: ", source);
            }
            return 1;
        }
        const clipState = this._getAudioClipState(clip.name);
        if (!clipState.passFilter) {
            return 0;
        }
        const defaultVolumeGain = source.type === AudioSource.AudioSourceType.Music ? this.defaultMusicVolumeGain : this.defaultEffectVolumeGain;
        return clipState.volumeGain !== -1 ? clipState.volumeGain : defaultVolumeGain;
    }

    public clearVolumeOnlyPassFilters() {
        this.volumeOnlyPassFilters = [];
    }

    public getDurationOf(clipName: string): number {
        const clip = this._getAudioClip(clipName);
        if (!clip) {
            warn(`Can't not find clip: ${clipName}`);
            return 0;
        }
        return clip.getDuration();
    }

    public async changeVoiceLanguage(language: VoiceLanguage) {
        if (!this._voiceAssetProvider) {
            warn('No voice asset provider is set');
            return;
        }
        if (language === this.currentVoiceLanguage) {
            return;
        }
        await this._voiceAssetProvider.loadLanguage(language);
    }

    public playMusic(type: TMusic, index: number = 0, loop = true, volumeRate: number = 1): AudioSource {
        const clip = this._assetProvider.get(type, AudioClip);
        if (!clip) {
            warn(`Can't not find clip: ${type}`);
            return null;
        }
        const audioSource = this._musicAudioSources[index] ?? (this._musicAudioSources[index] = this._getAudioSource(type));
        // if don't stop manually, setTimeout in player-web won't be cleared
        if (audioSource.playing) {
            audioSource.stop();
            this.emit(EventType.STOP_SOUND_EFFECT, audioSource);
        }
        audioSource.type = AudioSource.AudioSourceType.Music;
        audioSource.loop = loop;
        if (!this._playWithCheck(audioSource, clip, true)) {
            this.recycleAudioSource(audioSource);
        } else {
            this.setVolumeOf(audioSource, volumeRate);
        }
        return audioSource;
    }

    public findMusicIndex(type: TMusic): number {
        return this._musicAudioSources.findIndex((audioSource) => type === audioSource?.clip.name);
    }

    public stopAllMusic() {
        this._musicAudioSources.forEach((audioSource) => {
            audioSource?.stop();
            this.emit(EventType.STOP_SOUND_EFFECT, audioSource);
        });
    }

    public stopMusic(i: number = 0) {
        const audioSource = this._musicAudioSources[i];
        audioSource?.stop();
        this.emit(EventType.STOP_SOUND_EFFECT, audioSource);
    }

    public pauseAllMusic() {
        this._musicAudioSources.forEach((audioSource) => {
            audioSource?.pause();
            this.emit(EventType.STOP_SOUND_EFFECT, audioSource);
        });
    }

    public pauseMusic(i: number = 0) {
        const audioSource = this._musicAudioSources[i];
        audioSource?.pause();
        this.emit(EventType.STOP_SOUND_EFFECT, audioSource);
    }

    public resumeAllMusic() {
        this._musicAudioSources
            .filter((audioSource) => audioSource?.state === AudioSource.AudioState.PAUSED)
            .forEach((audioSource) => {
                audioSource.play();
                this.emit(EventType.PLAY_SOUND_EFFECT, audioSource);
            });
    }

    public resumeMusic(i: number = 0) {
        const audioSource = this._musicAudioSources[i];
        if (audioSource?.state === AudioSource.AudioState.PAUSED) {
            audioSource.play();
            this.emit(EventType.PLAY_SOUND_EFFECT, audioSource);
        }
    }

    public playEffect(type: TEffect, loop = false, startAt = 0, volumeRate: number = 1, lock = false): AudioSource {
        const clip = this._assetProvider.get(type, AudioClip);
        if (!clip) {
            warn(`Can't not find clip: ${type}`);
            return null;
        }
        return this._playEffect(type, clip, loop, startAt, volumeRate, lock);
    }

    public playVoice(type: TEffect, loop = false, startAt = 0, volumeRate: number = 1, lock = false): AudioSource {
        if (!this._voiceAssetProvider) {
            warn('No voice asset provider is set');
            return null;
        }
        const clip = this._voiceAssetProvider.get(type, AudioClip);
        if (!clip) {
            warn(`Can't not find clip: ${type}`);
            return null;
        }
        return this._playEffect(type, clip, loop, startAt, volumeRate, lock);
    }

    public stopEffect(clipName: TEffect | TVoice) {
        this.playingEffects.filter((effect) => effect.clip?.name === clipName).forEach((effect) => this.recycleAudioSource(effect));
    }

    public stopEffects(targetList: (TEffect | TVoice)[] = []) {
        this.playingEffects.filter((effect) => targetList.includes(effect.clip?.name as TEffect | TVoice)).forEach((effect) => this.recycleAudioSource(effect));
    }

    public stopAllEffects(exceptionList: (TEffect | TVoice)[] = []) {
        this.playingEffects
            .filter((effect) => !exceptionList.includes(effect.clip?.name as TEffect | TVoice))
            .forEach((effect) => this.recycleAudioSource(effect));
    }

    /**
     * 漸進調整音量
     * @param audioSource
     * @param duration 漸進時長 (單位：毫秒)
     * @param startVolume 起始音量
     * @param targetVolume 最終音量
     * @param stepDuration 每幾毫秒更新一次音量
     */
    public fadeInVolume(
        audioSource: AudioSource,
        duration: number,
        startVolume: number,
        targetVolume: number,
        stepDuration: number = 50,
    ): [Promise<any>, () => void, (isPause: boolean) => void] {
        const currentTime = Date.now();
        audioSource.fadeInStartTime = currentTime;
        this.setVolumeOf(audioSource, startVolume);
        let totalStep = duration / stepDuration;
        if (duration < stepDuration) {
            warn(`Duration ${duration} is less than step duration ${stepDuration}, using 1 step instead.`);
            totalStep = 1;
        }
        const perStepAdd = (targetVolume - startVolume) / totalStep;
        const [promise, stopFunc, pauseFunc] = Processor.run(
            function* step(this: AudioManager) {
                let i = 0;
                while (i < totalStep) {
                    if (audioSource.fadeInStartTime !== currentTime) {
                        return;
                    }
                    this.setVolumeOf(audioSource, audioSource.volumeRate + perStepAdd);
                    yield Delay.resolve(stepDuration);
                    i++;
                }
            }.call(this),
        );
        this._stopFadingVolumeCallbacks.set(audioSource, stopFunc);
        return [promise, stopFunc, pauseFunc];
    }

    public setVolumeOf(audioSource: AudioSource, targetVolume: number) {
        audioSource.volumeRate = targetVolume;
        this._syncAudioSourceVolume(audioSource);
    }

    public recycleAudioSource(audioSource: AudioSource) {
        if (!audioSource) {
            return;
        }
        if (!audioSource.isValid) {
            warn('attempt to recycle invalid audio source', audioSource);
            return;
        }
        const musicIndex = this.musicAudioSources.indexOf(audioSource);
        if (musicIndex !== -1) {
            this.musicAudioSources[musicIndex] = null;
        }
        Tween.stopAllByTarget(audioSource);
        // ensure fading volume process is stopped to avoid unexpected volume change after recycle
        if (this._stopFadingVolumeCallbacks.has(audioSource)) {
            const stopCallback = this._stopFadingVolumeCallbacks.get(audioSource);
            stopCallback();
            this._stopFadingVolumeCallbacks.delete(audioSource);
        }
        audioSource.stop();
        audioSource.currentTime = 0;
        audioSource.loop = false;
        this.emit(EventType.STOP_SOUND_EFFECT, audioSource);
        if (audioSource.clip) {
            const pool = this._getAudioSourcePool(audioSource.clip.name);
            pool.put(audioSource.node);
            const clipState = this._getAudioClipState(audioSource.clip.name);
            if (!clipState) {
                warn(`Can't find playing state of clip: ${audioSource.clip.name}`);
            } else {
                clipState.removePlaying(audioSource);
            }
        } else {
            audioSource.node.destroy();
        }
    }

    private _playEffect(type: TEffect | TVoice, clip: AudioClip, loop = false, startAt = 0, volumeRate: number = 1, lock = false): AudioSource {
        let audioSource: AudioSource = null;
        if (lock) {
            audioSource = this.playingEffects.find((playingEffect) => playingEffect.clip === clip);
        }
        audioSource ??= this._getAudioSource(type);
        audioSource.type = AudioSource.AudioSourceType.Effect;
        audioSource.loop = loop;
        if (!this._playWithCheck(audioSource, clip, false, startAt)) {
            this.recycleAudioSource(audioSource);
        } else {
            this.setVolumeOf(audioSource, volumeRate);
        }
        return audioSource;
    }

    /**
     * check if clip can play before actually play it
     * @param audioSource target audio source
     * @param clip clip to play
     * @returns if play success
     * @description not using audioSource.clip directly because clip may be null due to deferred clip assignment in **DeferredAudioSource.extension.ts**
     */
    private _playWithCheck(audioSource: AudioSource, clip: AudioClip, isMusic: boolean, startAt = 0): boolean {
        if (!clip) {
            return false;
        }

        // setup
        const originalClip = audioSource.clip;
        audioSource.clip = clip;
        audioSource.currentTime = startAt;

        // check playing state
        const clipState = this._getAudioClipState(clip.name);
        if (startAt >= clip.getDuration()) {
            return false;
        }
        if (!clipState.canPlay()) {
            return false;
        }

        // remove audio source from playing state of last clip if clip is changed
        if (originalClip && originalClip !== clip) {
            const lastClipState = this._getAudioClipState(originalClip.name);
            if (!lastClipState) {
                warn(`Can't find playing state of clip: ${originalClip.name}`);
            } else {
                lastClipState.removePlaying(audioSource);
            }
        }

        // actual play
        clipState.addPlaying(audioSource);
        audioSource.play();
        // must sync volume after actual play, because filterVolume always return 1 if clip is not assigned
        this._syncAudioSourceVolume(audioSource);
        this.emit(EventType.PLAY_SOUND_EFFECT, audioSource, clip);
        return true;
    }

    private _getAudioSource(name: string, needRecycle = true): AudioSource {
        const pool = this._getAudioSourcePool(name);
        const node =
            pool.get() ??
            (() => {
                const newNode = new Node();
                const audioSource = newNode.addComponent(AudioSource);
                audioSource.playOnAwake = false;
                if (needRecycle) {
                    // 監聽：播放完畢後回收
                    newNode.on(AudioSource.EventType.ENDED, () => {
                        this.recycleAudioSource(node.getComponent(AudioSource));
                    });
                }
                return newNode;
            })();
        this.audioSourceRoot.addChild(node);
        node.name = name;
        return node.getComponent(AudioSource);
    }

    private _updateEffectVolume(): void {
        this.playingEffects.forEach((audioSource) => this._syncAudioSourceVolume(audioSource));
    }

    private _updateMusicVolume(): void {
        this._musicAudioSources.forEach((audioSource) => this._syncAudioSourceVolume(audioSource));
    }

    private _syncAudioSourceVolume(audioSource: AudioSource) {
        if (!audioSource) {
            return;
        }
        const volume = audioSource.type === AudioSource.AudioSourceType.Music ? this.musicVolume : this.effectVolume;
        audioSource.volume = this.allMute ? 0 : volume * audioSource.volumeRate * this.filterVolume(audioSource);
    }

    private _getAudioClip(clipName: string): AudioClip {
        const clip = this._assetProvider.get(clipName, AudioClip) ?? this._voiceAssetProvider?.get(clipName, AudioClip);
        return clip;
    }

    private _getAudioClipState(clipName: string): AudioClipState {
        if (!this._audioClipStateMap[clipName]) {
            this._audioClipStateMap[clipName] = new AudioClipState(clipName, !this.isFilterEnabled);
        }
        return this._audioClipStateMap[clipName];
    }

    private _getAudioSourcePool(clipName: string): NodePool {
        if (!this._audioSourcePoolMap[clipName]) {
            this._audioSourcePoolMap[clipName] = new NodePool();
        }
        return this._audioSourcePoolMap[clipName];
    }
}
